/** ترتیب ثابت دسته‌ها برای flatten و نمایش خلاصه. */
import { AGENDA_UI_ENABLED } from '../constants/productFeatures';
import { getApiErrorMessage } from './apiError';

export const SEARCH_RESULT_KEYS = [
  'meetings',
  'rooms',
  'minutes',
  'agenda_items',
  'transcripts',
  'organizations',
];

export const SEARCH_CATEGORY_LABELS = {
  unified: 'همه',
  meetings: 'جلسات',
  rooms: 'اتاق‌ها',
  minutes: 'صورت‌جلسات',
  agenda_items: 'دستورجلسه',
  transcripts: 'رونوشت',
  organizations: 'سازمان‌ها',
};

export const SEARCH_SORT_OPTIONS = [
  { value: 'relevance', label: 'مرتبط‌ترین' },
  { value: 'newest', label: 'جدیدترین' },
  { value: 'oldest', label: 'قدیمی‌ترین' },
];

/**
 * ساختار خالی نتایج جستجو.
 * @returns {Record<string, unknown[]>}
 */
export function createEmptySearchResults() {
  return {
    meetings: [],
    rooms: [],
    minutes: [],
    agenda_items: [],
    transcripts: [],
    organizations: [],
  };
}

/**
 * شمارش صفر برای دسته‌های مجاز.
 * @param {string[]} [allowedKeys]
 */
export function createEmptySearchCounts(allowedKeys = SEARCH_RESULT_KEYS) {
  /** @type {Record<string, number>} */
  const counts = { all: 0 };
  for (const key of allowedKeys) {
    counts[key] = 0;
  }
  return counts;
}

/**
 * خطاهایی که در جستجو باید «بدون نتیجه» تلقی شوند، نه خطای واقعی.
 * @param {unknown} error
 */
export function isSearchEmptyApiError(error) {
  const err = error && typeof error === 'object' ? error : {};
  const status = /** @type {{ response?: { status?: number } }} */ (err).response?.status;
  if (status === 404) {
    return true;
  }
  const msg = getApiErrorMessage(error, '');
  return msg === 'منبع یافت نشد.' || /No query results for model/i.test(msg);
}

/**
 * پیام خطای قابل‌نمایش در باکس نتایج (بدون toast).
 * @param {unknown} error
 */
export function getSearchPanelErrorMessage(error) {
  const err = error && typeof error === 'object' ? error : {};
  const status = /** @type {{ response?: { status?: number } }} */ (err).response?.status;
  if (status === 429) {
    return 'تعداد درخواست بیش از حد مجاز است. لطفاً کمی بعد دوباره تلاش کنید.';
  }
  if (status === 403) {
    return 'دسترسی به جستجو در این محدوده برای شما فعال نیست.';
  }
  if (!status || status >= 500) {
    return 'جستجو در حال حاضر در دسترس نیست. لطفاً چند لحظه بعد دوباره تلاش کنید.';
  }
  return 'جستجو انجام نشد. لطفاً دوباره تلاش کنید.';
}

/**
 * انواع مدل مجاز بر اساس نقش کاربر.
 * @param {{ canViewTranscript?: boolean, includeOrganizations?: boolean }} opts
 * @returns {string[]}
 */
export function getAllowedSearchModelTypes({
  canViewTranscript = false,
  includeOrganizations = false,
} = {}) {
  const types = ['meetings', 'rooms', 'minutes'];
  if (AGENDA_UI_ENABLED) types.push('agenda_items');
  if (canViewTranscript) types.push('transcripts');
  if (includeOrganizations) types.push('organizations');
  return types;
}

/**
 * کلیدهای دسته‌ای که در UI خلاصه/فیلتر نمایش داده می‌شوند.
 * @param {{ canViewTranscript?: boolean, includeOrganizations?: boolean }} opts
 * @returns {string[]}
 */
export function getVisibleSearchCategoryKeys({
  canViewTranscript = false,
  includeOrganizations = false,
} = {}) {
  return getAllowedSearchModelTypes({ canViewTranscript, includeOrganizations });
}

/**
 * شمارش دسته‌ها از پاسخ API؛ در نبود counts از طول آرایه‌ها.
 * @param {object|null|undefined} payload پاسخ unifiedSearch یا خود results
 * @param {string[]} [allowedKeys]
 * @returns {{ all: number } & Record<string, number>}
 */
export function resolveSearchCounts(payload, allowedKeys = SEARCH_RESULT_KEYS) {
  const results = payload?.results && typeof payload.results === 'object'
    ? payload.results
    : payload;
  const apiCounts = payload?.counts
    ?? (results && typeof results === 'object' ? results.counts : null)
    ?? null;

  const counts = {};
  for (const key of allowedKeys) {
    const fromApi = apiCounts?.[key];
    if (fromApi != null && fromApi !== '') {
      counts[key] = Number(fromApi) || 0;
    } else {
      counts[key] = Array.isArray(results?.[key]) ? results[key].length : 0;
    }
  }

  // مجموع فقط روی دسته‌های مجاز تا با چیپ‌های نقش‌محور هم‌خوان باشد
  counts.all = allowedKeys.reduce((sum, key) => sum + (counts[key] || 0), 0);

  return counts;
}

/**
 * تخت‌سازی نتایج با حفظ ترتیب دسته‌ها.
 * @param {object|null|undefined} results
 * @param {{ max?: number, allowedKeys?: string[] }} [opts]
 * @returns {{ type: string, item: object }[]}
 */
export function flattenSearchResults(results, { max = Infinity, allowedKeys = SEARCH_RESULT_KEYS } = {}) {
  const flat = [];
  const source = results?.results && typeof results.results === 'object' && !Array.isArray(results.results)
    ? results.results
    : results;

  for (const key of allowedKeys) {
    const items = Array.isArray(source?.[key]) ? source[key] : [];
    for (const item of items) {
      flat.push({ type: key, item });
      if (flat.length >= max) return flat;
    }
  }
  return flat;
}

/**
 * نرمال‌سازی پاسخ unifiedSearch.
 * @param {object|null|undefined} res
 */
export function normalizeUnifiedSearchResponse(res) {
  const results = res?.results && typeof res.results === 'object' ? res.results : (res || null);
  const allowedKeys = SEARCH_RESULT_KEYS;
  return {
    query: res?.query ?? null,
    sort: res?.sort || 'relevance',
    results,
    counts: resolveSearchCounts(res, allowedKeys),
  };
}

/**
 * ساخت query string صفحه جستجو.
 * @param {{ q: string, mode?: string, sort?: string, page?: number|string }} opts
 * @returns {string}
 */
export function buildSearchPageSearch({ q, mode = 'unified', sort = 'relevance', page } = {}) {
  const params = new URLSearchParams();
  const trimmed = String(q ?? '').trim();
  if (trimmed) params.set('q', trimmed);
  params.set('mode', mode || 'unified');
  params.set('sort', sort || 'relevance');
  const pageNum = Number(page);
  if (pageNum > 1) params.set('page', String(pageNum));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

